import React, { useState } from 'react';
import { makePrioStyles } from '../../../theme/utils';
import Flex from '../../../components/Flex';
import { Collapse, List, Typography, notification } from 'antd';
import {
  Attendee,
  CalendarEvent,
  ProposedNewTime,
} from '../../../models/Calendar';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { PrioTheme } from '../../../theme/types';
import { useTheme } from 'react-jss';
import {
  Button,
  PillCRA as Pill,
  PrioSpinner,
} from '@prio365/prio365-react-library';
import { readableMessageEventTime } from '../util';
import { ProjectId } from '../../../models/Types';
import { apiAcceptProposedDateTime } from '../api';

const listItemHeight = 64;

const useStyles = makePrioStyles((theme) => ({
  root: {
    padding: theme.old.spacing.defaultPadding,
    height: '100%',
    overflow: 'auto',
  },
  title: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listItem: {
    paddingLeft: theme.old.spacing.defaultPadding,
    paddingRight: theme.old.spacing.defaultPadding,
    paddingTop: theme.old.spacing.unit(2),
    '&:hover': {
      backgroundColor: theme.old.palette.backgroundPalette.hover.content,
      cursor: 'pointer',
    },
    '& .ant-list-vertical .ant-list-item-meta': {
      marginBottom: 0,
    },
    height: listItemHeight,
    overflow: 'hidden',
    position: 'relative',
  },
  responseStatus: {
    width: 4,
    backgroundColor: theme.old.palette.chromaticPalette.grey,
    position: 'absolute',
    top: 2,
    bottom: 2,
    left: 0,
  },
  listItemMeta: {
    marginTop: theme.old.spacing.unit(2),
    '& .ant-list-item-meta-title': {
      marginBottom: 0,
      fontSize: theme.old.typography.fontSize.base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .ant-list-item-meta': {
      marginBottom: 0,
    },
    '& .ant-list-item-meta-content': {
      marginBottom: 0,
    },
  },
  row: {
    height: '100%',
  },
  collapse: {
    flex: 1,
    '&.ant-collapse': {
      border: 'none',
      backgroundColor: theme.old.palette.backgroundPalette.content,
    },
    '&.ant-collapse > .ant-collapse-item > .ant-collapse-header': {
      paddingLeft: 0,
    },
    '&.ant-collapse > .ant-collapse-item': {
      border: 'none',
    },
    '& .ant-collapse-content-box': {
      padding: 0,
    },
    '& .ant-collapse-content': {
      border: 'none',
    },
  },
  mailAddress: {
    fontSize: theme.old.typography.fontSize.label,
    color: theme.old.typography.colors.muted,
  },
}));

const mapStatusToColor: (
  status:
    | 'none'
    | 'organizer'
    | 'tentativelyAccepted'
    | 'accepted'
    | 'declined'
    | 'notResponded',
  theme: PrioTheme
) => string = (status, theme) => {
  switch (status) {
    case 'accepted':
      return theme.old.palette.chromaticPalette.green;
    case 'declined':
      return theme.old.palette.chromaticPalette.red;
    case 'tentativelyAccepted':
      return theme.old.palette.chromaticPalette.yellow;
    default:
      return theme.old.palette.chromaticPalette.grey;
  }
};

interface EventDetailsProps {
  className?: string;
  event: CalendarEvent;
  projectId: ProjectId;
  handleReloadMessage?: VoidFunction;
}

export const EventDetails: React.FC<EventDetailsProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { className, event, projectId, handleReloadMessage } = props;
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ Defaults
  const requiredAttendees =
    event?.attendees?.filter(
      (attendee) =>
        attendee.type === 'required' &&
        attendee.emailAddress.address !==
          event?.organizer?.emailAddress?.address
    ) ?? [];
  const optionalAttendees =
    event?.attendees?.filter(
      (attendee) =>
        attendee.type === 'optional' &&
        attendee.emailAddress.address !==
          event?.organizer?.emailAddress?.address
    ) ?? [];

  const numberOfNewTimeProposalsByRequiredAttendees = (
    event?.attendees || []
  ).filter((att) => att.proposedNewTime && att.type === 'required').length;

  const numberOfNewTimeProposalsByOptionalAttendees = (
    event?.attendees || []
  ).filter((att) => att.proposedNewTime && att.type === 'optional').length;
  //#endregion

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      {event?.organizer && (
        <Flex.Column>
          <Typography.Title level={4} className={classes.title}>
            {t('calendar:eventOverview.organizer')}
          </Typography.Title>
          <Typography.Text>
            {event.organizer.emailAddress.name ??
              event.organizer.emailAddress.address}
          </Typography.Text>
        </Flex.Column>
      )}
      {event?.attendees && (
        <Collapse className={classes.collapse}>
          {requiredAttendees.length > 0 && (
            <Collapse.Panel
              key="required"
              header={
                <Typography.Title level={4} className={classes.title}>
                  <Flex.Row
                    alignItems="center"
                    childrenGap={theme.spacing.small}
                  >
                    {t('calendar:eventOverview.attendee.type.required')}
                    {numberOfNewTimeProposalsByRequiredAttendees > 0 && (
                      <Pill type="primary" size="small">
                        {numberOfNewTimeProposalsByRequiredAttendees}
                      </Pill>
                    )}
                  </Flex.Row>
                </Typography.Title>
              }
            >
              <AttendeeList
                event={event}
                attendees={requiredAttendees}
                projectId={projectId}
                handleReloadMessage={handleReloadMessage}
              />
            </Collapse.Panel>
          )}
          {optionalAttendees.length > 0 && (
            <Collapse.Panel
              key="optional"
              header={
                <Typography.Title level={4} className={classes.title}>
                  <Flex.Row
                    alignItems="center"
                    childrenGap={theme.spacing.small}
                  >
                    {t('calendar:eventOverview.attendee.type.optional')}
                    {numberOfNewTimeProposalsByOptionalAttendees > 0 && (
                      <Pill type="primary" size="small">
                        {numberOfNewTimeProposalsByOptionalAttendees}
                      </Pill>
                    )}
                  </Flex.Row>
                </Typography.Title>
              }
            >
              <AttendeeList
                event={event}
                attendees={optionalAttendees}
                projectId={projectId}
                handleReloadMessage={handleReloadMessage}
              />
            </Collapse.Panel>
          )}
        </Collapse>
      )}
    </Flex.Column>
  );
};

export default EventDetails;

interface AttendeeListProps {
  event: CalendarEvent;
  attendees: Attendee[];
  projectId: ProjectId;
  handleReloadMessage?: VoidFunction;
}

const AttendeeList: React.FC<AttendeeListProps> = (props) => {
  //#region ------------------------------ Defaults
  const { event, attendees, projectId, handleReloadMessage } = props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onAccept = async (proposedNewTime: ProposedNewTime) => {
    setIsLoading(true);
    const result = await apiAcceptProposedDateTime(
      projectId,
      event,
      proposedNewTime
    );
    setIsLoading(false);
    if (result && handleReloadMessage) {
      handleReloadMessage();
      notification.open({
        message: t('common:success'),
        description: t(
          'calendar:messages.success.acceptingNewProposedTimeSuccess'
        ),
      });
    }
    if (!result) {
      notification.open({
        message: t('common:error'),
        description: t('calendar:messages.error.acceptingNewProposedTimeError'),
      });
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <List
      itemLayout="vertical"
      size="large"
      dataSource={attendees}
      renderItem={(attendee, index) => (
        <List.Item
          key={index}
          className={classes.listItem}
          style={attendee.proposedNewTime ? { height: 86 } : {}}
        >
          <Flex.Row className={classes.row} alignItems={'center'}>
            <div
              className={classes.responseStatus}
              style={{
                backgroundColor: mapStatusToColor(
                  attendee.status.response,
                  theme
                ),
              }}
            />
            <List.Item.Meta
              className={classes.listItemMeta}
              title={
                <>
                  {attendee.emailAddress.name ?? attendee.emailAddress.address}
                  {attendee.emailAddress.name && (
                    <span
                      className={classes.mailAddress}
                    >{` (${attendee.emailAddress.address})`}</span>
                  )}
                </>
              }
              description={
                <div>
                  <div>
                    {t(
                      `calendar:eventOverview.attendee.status.${attendee.status.response.toLowerCase()}`
                    )}
                  </div>
                  {attendee.proposedNewTime && (
                    <Flex.Row
                      alignItems="center"
                      childrenGap={theme.spacing.small}
                    >
                      <span
                        style={{
                          fontWeight: theme.font.fontWeight.bold,
                          color: theme.colors.application.typography.active,
                        }}
                      >
                        {`${t('calendar:eventOverview.attendee.newProposal')}:`}
                      </span>
                      <span>
                        {readableMessageEventTime({
                          id: 'newProposedTime',
                          start: attendee.proposedNewTime.start,
                          end: attendee.proposedNewTime.end,
                          isAllDay: false,
                          recurrence: null,
                        })}
                      </span>
                      {isLoading ? (
                        <PrioSpinner size="small" />
                      ) : (
                        <Button
                          size="small"
                          type="link"
                          iconProp={['fal', 'check']}
                          tooltip={t('common:actions.accept')}
                          onClick={() =>
                            onAccept(
                              attendee.proposedNewTime as ProposedNewTime
                            )
                          }
                        />
                      )}
                    </Flex.Row>
                  )}
                </div>
              }
            />
          </Flex.Row>
        </List.Item>
      )}
    ></List>
  );
};
